"use client"

import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import '@/styles/bauhaus.css'
import { useLanguage } from '@/i18n/LanguageProvider'
import { appLanguageToContentLocale } from '@/i18n/contentLocale'

export default function WorkshopGroupClient({ groupData, groupId }: { groupData: any, groupId: string }) {
  const { t, language } = useLanguage()
  const lang = appLanguageToContentLocale(language)
  const pLang = appLanguageToContentLocale(language)

  if (!groupData) {
    return <div>Group not found</div>
  }

  // 这里假设 groupData.groups[groupId] 有 bgGradient、summary、translators 等
  // groupData.poets 是诗人列表
  const groupInfo = groupData.groups[groupId]
  const poets = groupInfo.poets.map((poetId: string) => groupData.poets[poetId])

  // 你可以根据 groupInfo.bgGradient 或其它字段自定义配色
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1C1C1C] to-[#252525]">
      {/* 页面头部 */}
      <div className="pt-16 pb-16 relative bg-gradient-to-b from-[#1C1C1C] to-[#1C1C1C]">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="absolute w-full h-full bg-[radial-gradient(ellipse_at_center,_rgba(120,120,120,0.1)_0%,rgba(0,0,0,0)_70%)]"></div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* 返回链接 */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <Link 
              to="/Qiaodong/BPF/workshop"
              className="inline-flex items-center text-[#B9AE9A]/60 hover:text-[#B9AE9A] transition-colors duration-300 text-sm font-bauhaus"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t('bpf.workshop.group.back_to_workshop')}
            </Link>
          </motion.div>

          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-5xl font-bauhaus text-white tracking-tight mb-6"
            >
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#B9AE9A] to-[#8C4233]">
                {poets[0]?.names?.[pLang] || poets[0]?.names?.zh || ''} &{' '}
                {poets[1]?.names?.[pLang] || poets[1]?.names?.zh || ''}
              </span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-lg text-white/70 max-w-4xl mx-auto leading-relaxed"
            >
              {t('bpf.workshop.group.collaboration_works')}
            </motion.p>
          </div>
        </div>
      </div>

      {/* 主要内容 */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* 工作坊总结卡片 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-12"
        >
          <div className={`bg-gradient-to-br ${groupInfo.bgGradient || ''} rounded-2xl p-8 shadow-lg border-gray-100`}
               style={{borderWidth: '4px', borderRadius: '24px', borderColor: groupInfo.bgGradient?.match(/#([0-9A-Fa-f]{6})/)?.[0] || '#B9AE9A'}}>
            <div className="flex items-start space-x-6">
              <div className="flex-1">
                <h2 className="text-2xl font-bauhaus text-white mb-4">{t('bpf.workshop.group.workshop_summary')}</h2>
                <div className="text-white/90 leading-relaxed space-y-4">
                  <div className="mb-8 text-white/90 text-lg">
                    {groupInfo.summary?.[lang] || groupInfo.summary?.zh || ''}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* 诗人作品链接 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <h3 className="text-2xl font-bauhaus text-white mb-8 text-center">{t('bpf.workshop.group.poet_works')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {poets.map((poet: any, index: number) => (
              <Link key={poet.id} to={`/Qiaodong/BPF/workshop/${groupId}/${encodeURIComponent(poet.id)}`} className="contents">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                  className={`bg-gradient-to-br ${poet.bgGradient || ''} rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border-gray-100 group flex flex-col items-center justify-center text-center`}
                  style={{borderWidth: '3px', borderRadius: '20px', borderColor: poet.bgGradient?.match(/#([0-9A-Fa-f]{6})/)?.[0] || '#B9AE9A'}}>
                  <div>
                    <h4 className="text-xl font-semibold text-white mb-2">
                      {poet.names[lang] || poet.names.zh}
                    </h4>
                    <span className="text-white/80 text-base">{t('bpf.workshop.group.poet_works_collection')}</span>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  )
} 